import { Timestamp } from "@angular/fire/firestore";
import { format } from "date-fns";

export const lastModifiedDate = (date: string): string => {
  const now = date ? new Date(date) : new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = now.toLocaleString("default", { month: "short" });
  const year = now.getFullYear();
  return `${day} ${month} ${year}`;
};

export const convertTimestampToDate = (date: {
  nanoseconds?: number;
  seconds?: number;
  _nanoseconds?: number;
  _seconds?: number;
}) => {
  const seconds =
    typeof date?.seconds === "number" ? date.seconds : date?._seconds;
  const milliseconds = (seconds ?? 0) * 1000;
  return new Date(milliseconds);
};

export const convertDateToTimestamp = (date: Date) => {
  const milliseconds = date.getTime(); // Get the time in milliseconds since Unix epoch
  const seconds = Math.floor(milliseconds / 1000); // Convert milliseconds to seconds
  const nanoseconds = (milliseconds % 1000) * 1_000_000; // Calculate the remaining nanoseconds
  return { seconds, nanoseconds };
};

export const generateDate = (): string => {
  const now = new Date();
  now.setHours(now.getHours() + 6); // Set UTC offset to +06
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  const ampm = hours >= "12" ? "PM" : "AM";

  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds} ${ampm}`;
};

/**
 * Converts any date representation (ISO string, Unix timestamp, Date object,
 * or serialised Firestore Timestamp) into a JS Date in the viewer's local
 * timezone.  Returns null for null / undefined / unparseable values.
 *
 * Usage in templates: {{ safeParseDate(value) | date: 'dd MMM yyyy, HH:mm' }}
 * Angular DatePipe automatically converts a Date object to the browser's local
 * timezone, so no explicit timezone parameter is needed.
 */
export const safeParseDate = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    return new Date(value);
  }

  if (typeof value === "string") {
    // Append 'Z' when the ISO string has no timezone designator so the
    // browser always interprets it as UTC rather than as local time.
    let normalized = value.trim();
    if (
      normalized &&
      !normalized.endsWith("Z") &&
      !/[+\-]\d{2}:\d{2}$/.test(normalized) &&
      /^\d{4}-\d{2}-\d{2}T/.test(normalized)
    ) {
      normalized += "Z";
    }
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
  }

  // Handle Firestore Timestamp serialised as { _seconds, _nanoseconds } or { seconds, nanoseconds }
  if (typeof value === "object") {
    const ts = value as Record<string, unknown>;
    const secs =
      typeof ts["_seconds"] === "number"
        ? ts["_seconds"]
        : typeof ts["seconds"] === "number"
          ? ts["seconds"]
          : null;
    if (secs !== null) return new Date((secs as number) * 1000);
  }

  return null;
};

export const getMonthStartAndEnd = (
  date: string | Date,
): {
  start: string;
  end: string;
} => {
  const inputDate = new Date(date);

  const year = inputDate.getFullYear();
  const month = inputDate.getMonth();

  const startDate = new Date(year, month, 1);
  const formattedStartDate = `${("0" + startDate.getDate()).slice(-2)}/${("0" + (startDate.getMonth() + 1)).slice(-2)}/${startDate.getFullYear()}`;

  const endDate = new Date(year, month + 1, 0);
  const formattedEndDate = `${("0" + endDate.getDate()).slice(-2)}/${("0" + (endDate.getMonth() + 1)).slice(-2)}/${endDate.getFullYear()}`;

  return {
    start: formattedStartDate,
    end: formattedEndDate,
  };
};

export const formatToDDMMYYYY = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const parseFormattedDate = (dateString: string): string => {
  const [day, month, year] = dateString.split("-").map(Number);
  const utcDate = new Date(Date.UTC(year, month - 1, day));

  return utcDate.toISOString();
};

export const convertUtcToDhaka = (utcDateString: string): string => {
  const utcDate = new Date(utcDateString);
  const dhakaOffset = 6 * 60; // Dhaka is UTC+6 in minutes

  const dhakaDate = new Date(utcDate.getTime() + dhakaOffset * 60 * 1000);
  return dhakaDate.toISOString(); // Returns in ISO format
};

export const yearRange = (start: number, end: number) => {
  return [...Array(end - start + 1).keys()].map((x) => x + start);
};
export const dateFormatterDayMonthYear = (params: any): string => {
  if (!params.value) {
    return ""; // Handle null/undefined case
  }

  // Check if params.value is a Firestore timestamp
  if (
    params.value._seconds !== undefined &&
    params.value._nanoseconds !== undefined
  ) {
    const date = new Date(
      params.value._seconds * 1000 + params.value._nanoseconds / 1000000,
    );
    return formatToDDMMYYYYD(date);
  }

  // If params.value is a Date object
  if (params.value instanceof Date) {
    return formatToDDMMYYYYD(params.value);
  }

  const value = String(params.value); // Convert to string
  const dateParts = value.split("-");

  if (dateParts.length < 3) {
    return value; // Return original value if format is incorrect
  }

  const month = getMonthName(dateParts[1]);
  let year = dateParts[2].split(" ");

  if (year[0].length > 4) {
    year = dateParts[2].split("T");
  }
  return `${dateParts[0]}-${month}-${year[0]}`;
};

const getMonthName = (month: string): string => {
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return monthNames[parseInt(month, 10) - 1];
};

export const formatToDDMMYYYYD = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${getMonthName(month)}-${year}`;
};

// example function
export const updatedAtFormatToDDMMYYYY = (
  updatedAt?: string | Timestamp,
): string => {
  if (!updatedAt) return "";

  let date: Date;

  if (updatedAt instanceof Timestamp) {
    date = updatedAt.toDate();
  } else if (typeof updatedAt === "string") {
    // Match dd-MM-yyyy hh:mm:ss AM/PM
    const regex = /^(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2}):(\d{2}) (\w{2})$/;
    const match = updatedAt.match(regex);

    if (!match) return ""; // invalid format

    let [, day, month, year, hours, minutes, seconds, ampm] = match;

    let h = parseInt(hours, 10);
    const m = parseInt(minutes, 10);
    const s = parseInt(seconds, 10);
    const d = parseInt(day, 10);
    const mo = parseInt(month, 10) - 1; // JS months 0-based
    const y = parseInt(year, 10);

    if (ampm.toUpperCase() === "PM" && h < 12) h += 12;
    if (ampm.toUpperCase() === "AM" && h === 12) h = 0;

    date = new Date(y, mo, d, h, m, s);
  } else {
    return ""; // unsupported type
  }

  return format(date, "dd-MM-yyyy");
};

export const formatTimelineDateTime = (
  value: unknown,
): { date: string; time: string } => {
  const dateObj = safeParseDate(value);

  if (!dateObj) {
    return {
      date: "",
      time: "",
    };
  }

  const date = `${dateObj.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
  })}, ${dateObj.getFullYear()}`;

  const time = dateObj.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return { date, time };
};
